import { ownAmount, todayIso, type BudgetPeriod, type Dashboard, type PeriodItem, type Transaction } from '@fin/shared';
import { useState } from 'react';
import { planLineDefaults, type TxDefaults } from '../transactions/public.ts';

import { toast } from '../../components/ui.tsx';
import { NetworkError, authStore, del, patch, post, qs } from '../../lib/api.ts';
import { useCategories, useDebts, useGoals, useRates, useWallets } from '../../lib/data.ts';
import { money, rub } from '../../lib/format.ts';

import { enqueue } from '../../lib/offline.ts';
import { invalidateMoney, useQuery } from '../../lib/query.ts';

import { isHeld, isReserveLeg, RESERVE_NAME } from './model.tsx';

export function usePlanCheck(period: BudgetPeriod, onPay: (d: TxDefaults) => void, onChanged: () => void) {
  const wallets = useWallets();
  const rates = useRates();
  const cats = useCategories();
  const goals = useGoals();
  const debts = useDebts();
  const dash = useQuery<Dashboard>('/dashboard');
  const tx = useQuery<{ items: Transaction[] }>(`/transactions${qs({ from: period.startDate, to: period.endDate, limit: 1000 })}`);
  const [busy, setBusy] = useState<number | null>(null);
  const today = todayIso();
  const date = today < period.startDate ? period.startDate : today > period.endDate ? period.endDate : today;
  const mainId = dash.data?.primaryWallet?.id ?? authStore.get().user?.primaryWalletId;
  const main = wallets.data?.find((w) => w.id === mainId && !w.archived);

  /** posts an operation; without a connection it waits in the phone's queue */
  const send = async (path: '/transactions' | '/transfers', body: Record<string, unknown>, label: string) => {
    let queued = false;
    try {
      await post(path, body);
    } catch (e) {
      if (!(e instanceof NetworkError)) throw e;
      enqueue({ clientId: body.clientId as string, path, body, label });
      queued = true;
    }
    invalidateMoney();
    return queued;
  };

  const pay = async (it: PeriodItem) => {
    const d = planLineDefaults(it, goals.data ?? []);
    const reserve = isHeld(it) ? wallets.data?.find((w) => w.id === it.reserveWalletId && !w.archived) : undefined;
    const from = reserve ?? main;
    const amount = d.amount ?? 0;
    const sheet = (more?: Partial<TxDefaults>) => onPay({ ...d, ...more });
    // a transfer line kept in the receiving wallet's currency (as lines used to be): that sum is what arrives,
    // and the sending side is estimated by the rate of the latest transfers
    if (d.mode === 'transfer' && from && it.currency !== from.currency) {
      const to = wallets.data?.find((w) => w.id === d.toWalletId);
      const sent = to?.currency === it.currency && from.currency === rates.base ? rates.toBase(amount, it.currency) : null;
      return sheet(sent ? { amount: Math.round(sent), amountTo: amount } : {});
    }
    if (!from || amount <= 0 || from.currency !== it.currency) return sheet();
    const clientId = crypto.randomUUID();
    let path: '/transactions' | '/transfers';
    let body: Record<string, unknown>;
    let label: string;
    if (d.mode === 'transfer') {
      const to = wallets.data?.find((w) => w.id === d.toWalletId);
      if (!to || to.id === from.id || to.currency !== from.currency) return sheet();
      path = '/transfers';
      body = { fromWalletId: from.id, toWalletId: to.id, amount, date, note: it.name, periodItemId: it.id, clientId };
      label = `перевод ${money(amount, from.currency)}, ${from.name} → ${to.name}`;
    } else if (d.mode === 'debt') {
      const debt = debts.data?.find((x) => x.id === d.debtId);
      const cat = cats.data?.find((c) => c.kind === 'repayment' && !c.archived);
      const sum = Math.min(amount, debt?.remaining ?? 0);
      if (!debt || !cat || debt.currency !== from.currency || sum <= 0) return sheet();
      path = '/transactions';
      body = { walletId: from.id, categoryId: cat.id, amount: sum, date, note: it.name, contactId: debt.contactId, debtId: debt.id, periodItemId: it.id, clientId };
      label = `${money(sum, from.currency)}, ${debt.title}`;
    } else {
      // a line without a category pays the expense category of its name, as the period matches it
      const cat = cats.data?.find((c) => c.id === it.categoryId)
        ?? cats.data?.find((c) => c.kind === 'expense' && !c.archived && c.name.trim().toLowerCase() === it.name.trim().toLowerCase());
      if (!cat) return sheet();
      path = '/transactions';
      body = { walletId: from.id, categoryId: cat.id, amount, date, note: it.name, periodItemId: it.id, clientId };
      label = `${money(amount, from.currency)}, ${cat.name}`;
    }
    const queued = await send(path, body, label);
    toast(queued ? 'Нет связи — оплата сохранена на телефоне и отправится сама' : `Оплачено: ${label}`);
  };

  const unpay = async (it: PeriodItem) => {
    if (it.done) { await patch(`/period-items/${it.id}`, { done: false }); onChanged(); return; }
    // only operations entered for this line are taken back; matched by category or amount stay as they are
    const linked = (tx.data?.items ?? []).filter((t) => t.periodItemId === it.id && t.amount < 0 && !isReserveLeg(t, it));
    if (!linked.length) { toast('Строку закрыли операции периода — измените их в «Операциях»'); return; }
    const sum = linked.reduce((s, t) => s - t.amount, 0);
    if (!window.confirm(`Отменить оплату «${it.name}»? ${linked.length > 1 ? `Операции на ${rub(sum)} будут удалены` : `Операция на ${rub(sum)} будет удалена`}.`)) return;
    for (const t of linked) await del(`/transactions/${t.id}`);
    invalidateMoney();
    toast(it.held > 0 ? 'Оплата отменена, деньги остаются отложенными' : 'Оплата отменена');
  };

  /** moves the bill's money from the main account to `walletId` (or a new «Обязательные траты» wallet) until it is paid */
  const hold = async (it: PeriodItem, walletId: number | 'new') => {
    if (!main) throw new Error('Выберите основной счёт в «Счетах»');
    if (main.currency !== it.currency) throw new Error('Отложить можно только строку в валюте основного счёта');
    // only the owner's part waits for the bill; other people's parts come from them
    const own = ownAmount(it);
    if (own <= 0) throw new Error('У строки нет суммы');
    const to = walletId === 'new'
      ? await post<{ id: number; name: string }>('/wallets', { name: RESERVE_NAME, currency: main.currency, type: 'other' })
      : wallets.data?.find((w) => w.id === walletId);
    if (!to || to.id === main.id) throw new Error('Выберите другой счёт');
    const queued = await send('/transfers', {
      fromWalletId: main.id, toWalletId: to.id, amount: own, date, note: it.name, periodItemId: it.id, reserve: true, clientId: crypto.randomUUID(),
    }, `отложено ${money(own, main.currency)}, ${main.name} → ${to.name}`);
    toast(queued ? 'Нет связи — перевод сохранён на телефоне и отправится сам' : `Отложено ${money(own, main.currency)} на «${to.name}»`);
  };

  /** returns the money put aside to the main account; only while the bill itself is unpaid */
  const release = async (it: PeriodItem) => {
    const legs = (tx.data?.items ?? []).filter((t) => t.periodItemId === it.id && t.amount < 0 && isReserveLeg(t, it));
    if (!legs.length) { toast('Перевод не найден — измените его в «Операциях»'); return; }
    if (!window.confirm(`Вернуть ${rub(it.held)} на основной счёт? «${it.name}» снова будет ждать оплаты с него.`)) return;
    for (const t of legs) await del(`/transactions/${t.id}`);
    invalidateMoney();
    toast('Деньги возвращены на основной счёт');
  };

  const guard = async (it: PeriodItem, fn: () => Promise<void>) => {
    if (busy !== null) return;
    setBusy(it.id);
    try { await fn(); } catch (e) { toast((e as Error).message, 'error'); } finally { setBusy(null); }
  };

  return {
    busy,
    main,
    toggle: (it: PeriodItem) => guard(it, () => (it.paid ? unpay(it) : pay(it))),
    hold: (it: PeriodItem, walletId: number | 'new') => guard(it, () => hold(it, walletId)),
    release: (it: PeriodItem) => guard(it, () => release(it)),
  };
}
